import React, { useEffect, useRef, useState } from 'react';
import '../styles/CarouselDots.css';

const VISIBLE = 5;
const BUFFER = 1;
const STEP_PX = 22; // must match --slot in CarouselDots.css
const BASE_TX = -BUFFER * STEP_PX;
const ANIM_MS = 280;

function wrapIndex(index, total) {
  return ((index % total) + total) % total;
}

function shortestSteps(from, to, total) {
  const forward = (to - from + total) % total;
  const backward = (from - to + total) % total;
  if (forward === 0) return { direction: 0, steps: 0 };
  if (forward <= backward) return { direction: 1, steps: forward };
  return { direction: -1, steps: backward };
}

function CarouselDots({ totalItems, currentIndex, onDotClick, visibleCount = VISIBLE }) {
  const half = Math.floor(visibleCount / 2);
  const [displayCenter, setDisplayCenter] = useState(currentIndex);
  const [tx, setTx] = useState(BASE_TX);
  const [instant, setInstant] = useState(false);
  const animatingRef = useRef(false);
  const displayCenterRef = useRef(displayCenter);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => {
    displayCenterRef.current = displayCenter;
  }, [displayCenter]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (currentIndex === displayCenterRef.current || animatingRef.current) {
      return;
    }

    const from = displayCenterRef.current;
    const { direction, steps } = shortestSteps(from, currentIndex, totalItems);

    if (direction === 0) return;

    if (steps > 1) {
      setInstant(true);
      setDisplayCenter(currentIndex);
      setTx(BASE_TX);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setInstant(false));
      });
      return;
    }

    animatingRef.current = true;
    setInstant(false);
    setTx(direction > 0 ? BASE_TX - STEP_PX : BASE_TX + STEP_PX);

    const timer = window.setTimeout(() => {
      setInstant(true);
      const settled = currentIndexRef.current;
      setDisplayCenter(settled);
      displayCenterRef.current = settled;
      setTx(BASE_TX);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setInstant(false);
          animatingRef.current = false;
          if (currentIndexRef.current !== settled) {
            setDisplayCenter(currentIndexRef.current);
            displayCenterRef.current = currentIndexRef.current;
          }
        });
      });
    }, ANIM_MS);

    return () => window.clearTimeout(timer);
  }, [currentIndex, totalItems]);

  if (totalItems <= 1) return null;

  const renderCount = visibleCount + BUFFER * 2;
  const slots = Array.from({ length: renderCount }, (_, i) => {
    const offset = i - half - BUFFER;
    return {
      slot: i,
      offset,
      itemIndex: wrapIndex(displayCenter + offset, totalItems),
      distance: Math.abs(offset),
    };
  });

  const handleSlotClick = (offset, itemIndex) => {
    if (offset === 0 || animatingRef.current) return;
    onDotClick(itemIndex);
  };

  return (
    <div className="carousel-dots" role="tablist" aria-label="Carousel pagination">
      <div className="carousel-dots-viewport">
        <div
          className={`carousel-dots-track${instant ? ' is-instant' : ''}`}
          style={{ transform: `translateX(${tx}px)` }}
        >
          {slots.map(({ slot, offset, itemIndex, distance }) => (
            <button
              key={slot}
              type="button"
              role="tab"
              aria-selected={offset === 0}
              tabIndex={Math.abs(offset) > half ? -1 : 0}
              className={`dot distance-${Math.min(distance, 2)}${offset === 0 ? ' is-center' : ''}`}
              onClick={() => handleSlotClick(offset, itemIndex)}
              aria-label={`Go to item ${itemIndex + 1}`}
            />
          ))}
        </div>
        <div className="carousel-dots-active" aria-hidden="true" />
      </div>
    </div>
  );
}

export default CarouselDots;
